import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle
} from '@mui/material'
import { Device, Project, RuuviTag } from '../types'
import { AlertGroup, AlertTemplate } from '../types/Alerts'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { LoadingButton } from '@mui/lab'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { useState } from 'react'
import useAlertRules from '../hooks/useAlertRules'
import { AlertRuleDataGridRow } from '../types/AlertRuleDataGridRow'

export interface AlertRuleRemoveDialogProps extends Omit<DialogProps, 'children' | 'onClose'> {
  alertRules: AlertGroup[]
  alertTemplates: AlertTemplate[]
  rule: string
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  useRedux?: boolean
  onClose?: () => void
  beforeSubmit?: (input: AlertRuleDataGridRow[]) => void
  afterSubmit?: (alertRules: AlertGroup[]) => void
}

export default function AlertRuleRemoveDialog({
  project,
  ruuviTag,
  device,
  alertRules,
  alertTemplates,
  rule,
  open,
  maxWidth = 'sm',
  fullWidth = true,
  useRedux = true,
  onClose,
  beforeSubmit,
  afterSubmit,
  ...props
}: AlertRuleRemoveDialogProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * Indicates if the alert rule is being deleted.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * The alerts rows.
   */
  const { enabledAlertRules, updateAlertRules } = useAlertRules({
    project,
    device,
    ruuviTag,
    alertTemplates,
    alertRules,
    useRedux
  })

  /**
   * Handle the submit.
   */
  const handleSubmit = async () => {
    try {
      const input = enabledAlertRules.map((row) =>
        row.id === rule ? { ...row, enabled: false } : row
      )
      setSubmitting(true)
      beforeSubmit?.(input)

      const updatedAlertRules = await updateAlertRules(input)
      setSubmitting(false)
      dispatch(createSuccessNotification(t('disableAlertRuleSuccess')))
      afterSubmit?.(updatedAlertRules)
    } catch (_error) {
      setSubmitting(false)
      dispatch(createErrorNotification(t('disableAlertRuleError')))
    }
  }

  return (
    <Dialog open={open} maxWidth={maxWidth} fullWidth={fullWidth} {...props} onClose={onClose}>
      <DialogTitle>{t('deleteAlertRule')}</DialogTitle>
      <DialogContent>
        <Alert severity="error">{t('deleteAlertRuleConfirmation')}</Alert>
      </DialogContent>
      <DialogActions sx={{ pt: 0 }}>
        <Button variant="text" color="inherit" disabled={isSubmitting} onClick={onClose}>
          {t('cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {t('confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
